import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, MapPin, Mail, Phone, Clock, Shield,
  ChevronDown, ChevronRight, Send, CheckCircle2, HelpCircle
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { contactInfo } from '../data/mock';

const ContactPage = () => {
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    investorType: '',
    investmentRange: '',
    marketingConsent: false,
    serviceConsent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // FAQ state
  const [openFaq, setOpenFaq] = useState(null);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }
    }
  };

  // Handle form changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone || null,
          investor_type: formData.investorType || null,
          investment_range: formData.investmentRange || null,
          interests: [],
          message: null
        }),
      });

      if (!response.ok) throw new Error('Failed to submit inquiry');

      setIsSubmitting(false);
      setSubmitSuccess(true);
      
      setTimeout(() => {
        setSubmitSuccess(false);
        setFormData({
          firstName: '', lastName: '', email: '', phone: '',
          investorType: '', investmentRange: '', marketingConsent: false, serviceConsent: false
        });
      }, 3000);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      setIsSubmitting(false);
      alert('There was an error submitting your inquiry. Please try again or contact us directly.');
    }
  };

  // FAQ data
  const faqs = [
    {
      question: 'Do you only work with investors?',
      answer: 'Our private equity offerings are available only to investors as defined by SEC regulations. Investment terms are provided solely through official offering documents.',
      link: null
    },
    {
      question: 'How can I learn more about your investment strategy?',
      answer: 'You can review our value-add investment approach on our Investment Strategy page.',
      link: { text: 'View Investment Strategy', href: '/strategy' }
    },
    {
      question: 'Where can I see past investment examples?',
      answer: 'Our Case Studies page highlights selected property-level outcomes and execution strategies.',
      link: { text: 'View Case Studies', href: '/case-studies' }
    },
    {
      question: 'Where can I review fund structures and terms?',
      answer: 'Detailed fund information is available on our Fund Terms page and in official PPM documents.',
      link: { text: 'View Fund Terms', href: '/fund-terms' }
    }
  ];

  return (
    <>
      <Helmet>
        <title>Contact Our Real Estate Investment Team | ASAP Capital</title>
        <meta 
          name="description" 
          content="Contact ASAP Capital's investor relations team for private equity real estate investment opportunities in Houston, Texas." 
        />
        <meta name="keywords" content="contact ASAP Capital, Houston real estate investment, investor relations, investor contact" />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="/contact" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== FORM SECTION - CLEAN & FOCUSED ==================== */}
          <section className="relative pt-24 pb-8 lg:pt-28 lg:pb-10 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#F97316]/5 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-blue-500/5 rounded-full blur-[100px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
              >
                {/* Page Header - Clean and minimal */}
                <motion.div variants={itemVariants} className="text-center mb-6">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">
                    Start Your Investment Inquiry
                  </h1>
                  <p className="text-white/50 text-sm sm:text-base max-w-lg mx-auto">
                    Connect with our investor relations team to explore real estate opportunities in Greater Houston.
                  </p>
                </motion.div>

                {/* Form Card - Clean, form-focused */}
                <motion.div variants={itemVariants}>
                  <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-5 sm:p-6 lg:p-8 border border-white/10 shadow-xl">
                    {submitSuccess ? (
                      <motion.div 
                        className="text-center py-10"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                      >
                        <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                          <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Thank You!</h3>
                        <p className="text-white/50">Your inquiry has been submitted. Our team will respond shortly.</p>
                      </motion.div>
                    ) : (
                      <form onSubmit={handleSubmit}>
                        {/* Form Grid - 2 columns on larger screens */}
                        <div className="grid sm:grid-cols-2 gap-x-4 lg:gap-x-5 gap-y-4">
                          {/* First Name */}
                          <div>
                            <label className="block text-white/70 text-sm mb-1.5">First Name *</label>
                            <input
                              type="text"
                              name="firstName"
                              value={formData.firstName}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#F97316]/50 focus:ring-1 focus:ring-[#F97316]/30 transition-all"
                              placeholder="John"
                              data-testid="contact-first-name"
                            />
                          </div>
                          
                          {/* Last Name */}
                          <div>
                            <label className="block text-white/70 text-sm mb-1.5">Last Name *</label>
                            <input
                              type="text"
                              name="lastName"
                              value={formData.lastName}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#F97316]/50 focus:ring-1 focus:ring-[#F97316]/30 transition-all"
                              placeholder="Doe"
                              data-testid="contact-last-name"
                            />
                          </div>

                          {/* Email */}
                          <div>
                            <label className="block text-white/70 text-sm mb-1.5">Email *</label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#F97316]/50 focus:ring-1 focus:ring-[#F97316]/30 transition-all"
                              placeholder="john@example.com"
                              data-testid="contact-email"
                            />
                          </div>
                          
                          {/* Phone */}
                          <div>
                            <label className="block text-white/70 text-sm mb-1.5">Phone (Optional)</label>
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#F97316]/50 focus:ring-1 focus:ring-[#F97316]/30 transition-all"
                              placeholder="(555) 123-4567"
                              data-testid="contact-phone"
                            />
                          </div>

                          {/* Investor Type */}
                          <div>
                            <label className="block text-white/70 text-sm mb-1.5">Investor Type</label>
                            <select
                              name="investorType"
                              value={formData.investorType}
                              onChange={handleChange}
                              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#F97316]/50 focus:ring-1 focus:ring-[#F97316]/30 transition-all appearance-none cursor-pointer"
                              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='rgba(255,255,255,0.4)'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '18px' }}
                              data-testid="contact-investor-type"
                            >
                              <option value="" className="bg-[#0A1628]">Select investor type</option>
                              <option value="accredited" className="bg-[#0A1628]">Accredited Investor</option>
                              <option value="family-office" className="bg-[#0A1628]">Family Office</option>
                              <option value="institutional" className="bg-[#0A1628]">Institutional Investor</option>
                              <option value="broker-lender" className="bg-[#0A1628]">Broker / Lender</option>
                              <option value="other" className="bg-[#0A1628]">Other</option>
                            </select>
                          </div>

                          {/* Investment Range */}
                          <div>
                            <label className="block text-white/70 text-sm mb-1.5">Anticipated Investment</label>
                            <select
                              name="investmentRange"
                              value={formData.investmentRange}
                              onChange={handleChange}
                              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#F97316]/50 focus:ring-1 focus:ring-[#F97316]/30 transition-all appearance-none cursor-pointer"
                              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='rgba(255,255,255,0.4)'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '18px' }}
                              data-testid="contact-investment-range"
                            >
                              <option value="" className="bg-[#0A1628]">Select range</option>
                              <option value="under-250k" className="bg-[#0A1628]">Under $250K</option>
                              <option value="250k-500k" className="bg-[#0A1628]">$250K – $500K</option>
                              <option value="500k-1m" className="bg-[#0A1628]">$500K – $1M</option>
                              <option value="1m-plus" className="bg-[#0A1628]">$1M+</option>
                            </select>
                          </div>
                        </div>

                        {/* SMS Consent Checkboxes - Two separate checkboxes */}
                        <div className="mt-4 space-y-3">
                          {/* Checkbox 1 - Marketing Consent */}
                          <div className="bg-white/5 rounded-lg p-3 sm:p-4 border border-white/10">
                            <label className="flex items-start gap-3 cursor-pointer group" htmlFor="marketing-consent-checkbox">
                              <div className="relative flex-shrink-0 mt-0.5">
                                <input
                                  type="checkbox"
                                  name="marketingConsent"
                                  checked={formData.marketingConsent}
                                  onChange={handleChange}
                                  className="sr-only peer"
                                  data-testid="contact-marketing-consent"
                                  id="marketing-consent-checkbox"
                                />
                                <div 
                                  className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-all duration-200 ${
                                    formData.marketingConsent 
                                      ? 'bg-[#F97316] border-[#F97316]' 
                                      : 'border-white/40 bg-transparent hover:border-[#F97316]/60'
                                  }`}
                                >
                                  {formData.marketingConsent && (
                                    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                  )}
                                </div>
                              </div>
                              <span className="text-white/60 text-xs leading-relaxed">
                                I consent to receive marketing text messages from ASAP Capital at the phone number provided. Message frequency: up to 6 messages per month. Message & data rates may apply. Text HELP for assistance, reply STOP to opt out.
                              </span>
                            </label>
                          </div>

                          {/* Checkbox 2 - Service/Non-Marketing Consent */}
                          <div className="bg-white/5 rounded-lg p-3 sm:p-4 border border-white/10">
                            <label className="flex items-start gap-3 cursor-pointer group" htmlFor="service-consent-checkbox">
                              <div className="relative flex-shrink-0 mt-0.5">
                                <input
                                  type="checkbox"
                                  name="serviceConsent"
                                  checked={formData.serviceConsent}
                                  onChange={handleChange}
                                  className="sr-only peer"
                                  data-testid="contact-service-consent"
                                  id="service-consent-checkbox"
                                />
                                <div 
                                  className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-all duration-200 ${
                                    formData.serviceConsent 
                                      ? 'bg-[#F97316] border-[#F97316]' 
                                      : 'border-white/40 bg-transparent hover:border-[#F97316]/60'
                                  }`}
                                >
                                  {formData.serviceConsent && (
                                    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                  )}
                                </div>
                              </div>
                              <span className="text-white/60 text-xs leading-relaxed">
                                I consent to receive non-marketing text messages from ASAP Capital regarding my inquiry, consultation scheduling, appointment confirmations, and service-related updates. Message frequency: up to 6 messages per month (plus replies to my requests). Message & data rates may apply. Text HELP for assistance, reply STOP to opt out.
                              </span>
                            </label>
                          </div>

                          {/* Terms Agreement Text */}
                          <p className="text-white/50 text-xs text-center pt-1">
                            By submitting this form, you agree to our{' '}
                            <Link to="/terms" className="text-[#F97316] hover:underline">Terms of Service</Link>
                            {' '}and{' '}
                            <Link to="/privacy" className="text-[#F97316] hover:underline">Privacy Policy</Link>.
                          </p>
                        </div>

                        {/* Submit Button - Full width, aligned with form */}
                        <div className="mt-5">
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="cta-contact"
                            data-testid="contact-submit-btn"
                          >
                            {isSubmitting ? (
                              <>
                                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Submitting...
                              </>
                            ) : (
                              <>
                                Submit Inquiry
                                <ArrowRight className="h-5 w-5" />
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== OFFICE LOCATION SECTION (BELOW FORM) ==================== */}
          <section className="py-10 lg:py-12 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d] border-t border-white/5">
            <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={containerVariants}
              >
                <motion.div variants={itemVariants} className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                    <MapPin className="h-5 w-5 text-blue-400" />
                  </div>
                  <h2 className="text-xl lg:text-2xl font-bold text-white">Office Location</h2>
                </motion.div>

                <div className="grid lg:grid-cols-[1fr_1.8fr] gap-5">
                  {/* Contact Details Card */}
                  <motion.div variants={itemVariants} className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-5 border border-white/10">
                    <h3 className="text-lg font-bold text-white mb-4">ASAP Capital</h3>
                    
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <MapPin className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-white/70">{contactInfo.address.city}, {contactInfo.address.state}</p>
                          <p className="text-white/50 text-sm">United States</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <Mail className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                        <div>
                          <p className="text-white/40 text-xs mb-0.5">Investor Relations</p>
                          <a href={`mailto:${contactInfo.email}`} className="text-white/70 hover:text-[#D4AF37] transition-colors text-sm">
                            {contactInfo.email}
                          </a>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <Phone className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                        <div>
                          <p className="text-white/40 text-xs mb-0.5">Phone</p>
                          <a href={`tel:${contactInfo.phone.replace(/[^0-9]/g, '')}`} className="text-white/70 hover:text-[#D4AF37] transition-colors text-sm">
                            {contactInfo.phone}
                          </a>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Clock className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-white/40 text-xs mb-0.5">Business Hours</p>
                          <p className="text-white/70 text-sm">Monday – Friday, 9 AM – 5 PM CST</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Map Embed - Full width of container, no awkward padding */}
                  <motion.div variants={itemVariants} className="rounded-xl overflow-hidden border border-white/10 h-[240px] lg:h-auto">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d443088.3394428761!2d-95.7560246!3d29.7604267!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640b8b4488d8501%3A0xca0d02def365053b!2sHouston%2C%20TX!5e0!3m2!1sen!2sus!4v1640000000000!5m2!1sen!2sus"
                      width="100%"
                      height="100%"
                      style={{ border: 0, minHeight: '240px' }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="ASAP Capital Office Location"
                    />
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== FAQ SECTION ==================== */}
          <section className="py-12 lg:py-14 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                    <HelpCircle className="h-5 w-5 text-purple-400" />
                  </div>
                  <h2 className="text-xl lg:text-2xl font-bold text-white">
                    FAQs Before You Contact Us
                  </h2>
                </motion.div>

                <div className="space-y-3">
                  {faqs.map((faq, index) => (
                    <motion.div
                      key={index}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl border border-white/10 overflow-hidden"
                      variants={itemVariants}
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        className="w-full flex items-center justify-between p-4 text-left"
                      >
                        <span className="text-white font-medium text-sm pr-4">{faq.question}</span>
                        <ChevronDown 
                          className={`h-5 w-5 text-[#D4AF37] flex-shrink-0 transition-transform duration-200 ${openFaq === index ? 'rotate-180' : ''}`}
                        />
                      </button>
                      <AnimatePresence>
                        {openFaq === index && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <div className="px-4 pb-4 pt-0">
                              <p className="text-white/50 text-sm mb-2">{faq.answer}</p>
                              {faq.link && (
                                <Link 
                                  to={faq.link.href}
                                  className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-medium hover:gap-3 transition-all"
                                >
                                  {faq.link.text}
                                  <ChevronRight className="h-4 w-4" />
                                </Link>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>

                <motion.div className="text-center mt-6" variants={itemVariants}>
                  <Link to="/faq">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full px-6 text-sm">
                      View All FAQs
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== COMPLIANCE DISCLAIMER ==================== */}
          <section className="py-6 relative">
            <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="bg-white/[0.02] border border-white/5 rounded-xl p-4"
              >
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-white/30 flex-shrink-0 mt-0.5" />
                  <p className="text-white/40 text-xs leading-relaxed">
                    <strong className="text-white/50">Important Disclosure:</strong> Information 
                    submitted through this contact form does not constitute an offer to sell or a 
                    solicitation to buy any securities. Investment opportunities are available only 
                    to investors through official offering documents, including the Private 
                    Placement Memorandum (PPM). All investments involve risk, including possible loss 
                    of principal.
                  </p>
                </div>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ContactPage;
